import { Modal } from '@foam/buoy/src/components/Modal/Modal';
import { useRouter } from 'next/router';
import { useEffect, useMemo } from 'react';
import { match } from 'ts-pattern';
import authApi from '~/authApi';
import {
  getMetricsSource,
  getPlatformMetrics,
} from '~/components/CreatorChannelMetrics';
import {
  FEATURE_FLAGS,
  SOCIAL_PLATFORMS,
  SOCIAL_PLATFORMS_NAMES,
  USER_ROLES,
} from '~/constants';
import { DEMOGRAPHICS_SOURCES } from '~/constants/demographicsSources';
import { SupportedCharts, SupportedPlatforms } from '~/constants/platforms';
import { useGetBenchmarksById } from '~/hooks/api/benchmarks/useGetBenchmarksById';
import { useGetMediaKit } from '~/hooks/api/media-kits/get';
import { useFetchCreator } from '~/hooks/api/useFetchCreator';
import { useFeature } from '~/hooks/useFeatureFlag/useFeature';
import {
  getEmptyCharts,
  getInsightId,
  getIsEmptyData,
  getPlatformStatus,
} from '~/screens/CreatorAudienceDemographics';
import { useInsights } from '~/screens/CreatorAudienceDemographics/GenderDistribution';
import { TalentProfileProperties } from '~/screens/PageBuilder/blocks/TalentProfile';
import { formatDate } from '~/utils';

import { AnalyticsEmptyState } from './AnalyticsEmptyState';
import { DemographicsEmptyState } from './DemographicsEmptyState';
import { DisconnectedCard } from './DisconnectedCard';
import { MetricsEmptyState } from './MetricsEmptyState';
import { PlatformDemographics } from './PlatformDemographics';
import { PlatformMetrics } from './PlatformMetrics';
import { usePlatformAnalyticsStore } from './usePlatformAnalyticsStore';

const demographics = [
  'gender',
  'age',
  'country',
  'growthRate',
] as SupportedCharts[];

export const ModalPlatformAnalytics = () => {
  const router = useRouter();
  const shareable = useGetMediaKit({ id: router.query.id as string });
  const talentProfile = shareable.data?.blocks.find(
    (s) => s.component === 'TalentProfile',
  );

  const isCreator = authApi.user.roles.includes(USER_ROLES.creator);
  const { isEnabled: isModashAudienceDemographicsEnabled } = useFeature(
    FEATURE_FLAGS.foamModashAudienceDemographics,
  );

  const talentProfileProperties = talentProfile?.properties as
    | TalentProfileProperties
    | undefined;

  // The API flattens the nested `creator` snapshot, so `creator.id` is undefined.
  const creator = useFetchCreator(
    talentProfileProperties?.creatorId ?? talentProfileProperties?.creator?.id,
  );

  const {
    isModalOpen,
    platform,
    reset,
    selectedDemographics,
    selectedMetrics,
    setAllAnalytics,
    callback,
  } = usePlatformAnalyticsStore();

  const insightId =
    getInsightId({
      creator: creator.data,
      platform: platform as SOCIAL_PLATFORMS,
    }) || '';

  const insights = useInsights({
    insightId,
    platform: platform as SOCIAL_PLATFORMS,
  });

  const benchmarks = useGetBenchmarksById({
    id: creator.data?.benchmark.id,
    creatorId: creator.data?.id,
  });

  const creatorPlatformMetrics = useMemo(() => {
    if (!benchmarks.isSuccess) return;

    return getPlatformMetrics(benchmarks.data);
  }, [benchmarks.data, benchmarks.isSuccess]);

  const metricsToShow = (
    creatorPlatformMetrics?.[platform as SOCIAL_PLATFORMS] ?? []
  ).filter((metric) => metric.value != undefined);

  const connectionStatus = useMemo(() => {
    // For creators, check the specific platform status from insights
    if (isCreator && insights?.data) {
      const insightsData = insights.data as any;
      const creatorData = insightsData.creator;

      if (creatorData) {
        if (platform === SOCIAL_PLATFORMS.instagram) {
          return creatorData.igStatus;
        }
        if (platform === SOCIAL_PLATFORMS.tiktok) {
          return creatorData.ttStatus;
        }
        if (platform === SOCIAL_PLATFORMS.youtube) {
          return creatorData.ytStatus;
        }
      }
    }

    // For non-creators, use the existing logic
    return getPlatformStatus({
      creator: creator?.data,
      platform: platform as SOCIAL_PLATFORMS,
    });
  }, [isCreator, insights?.data, creator?.data, platform]);

  const demographicsSource = useMemo(() => {
    if (platform === SOCIAL_PLATFORMS.youtube) {
      return creator.data?.ytChannels?.[0]?.demographicsSource;
    }

    const insightsData = insights.data as
      | { demographicsSource?: string }
      | undefined;
    return insightsData?.demographicsSource;
  }, [creator.data?.ytChannels, insights.data, platform]);

  useEffect(() => {
    return () => {
      // Reset the modal when it unmounts
      reset();
    };
  }, []);

  const getLastUpdatedDate = useMemo(() => {
    if (!benchmarks.isSuccess) return;
    const data = benchmarks.data;

    if (!data) return;
    if (platform === SOCIAL_PLATFORMS.instagram) return data.igLastUpdatedAt;
    if (platform === SOCIAL_PLATFORMS.tiktok) return data.ttLastUpdatedAt;
    if (platform === SOCIAL_PLATFORMS.youtube) return data.ytLastUpdatedAt;
  }, [benchmarks.data, benchmarks.isSuccess, platform]);

  const handleSave = () => {
    if (typeof callback === 'function') {
      callback(selectedDemographics, selectedMetrics);
    }
    reset();
  };

  const isDisconnected = connectionStatus === 'disconnected';
  const isEstimatedMetricsEnabled =
    isModashAudienceDemographicsEnabled &&
    demographicsSource === DEMOGRAPHICS_SOURCES.modashEstimated;
  const isPublicDemographics =
    demographicsSource === DEMOGRAPHICS_SOURCES.tiktokPublic;
  const shouldBypassConnectModalForOffPlatformDemographics =
    connectionStatus === 'off-platform' &&
    (isEstimatedMetricsEnabled || isPublicDemographics);
  /**
   * Benchmarks carry their own source, independent of the demographics one: a
   * TikTok account can have public demographics and Modash-estimated metrics.
   * Estimated metrics stay out of media kits either way (FOAM-23261).
   */
  const benchmarksSource = benchmarks.isSuccess
    ? getMetricsSource(benchmarks.data)[platform as SupportedPlatforms]
    : undefined;
  const isEstimatedPerformanceMetricsUnavailable =
    isEstimatedMetricsEnabled ||
    benchmarksSource === DEMOGRAPHICS_SOURCES.modashEstimated;
  const isConnected =
    connectionStatus === 'connected' ||
    isDisconnected ||
    shouldBypassConnectModalForOffPlatformDemographics;

  const isEmpty = getIsEmptyData(insights?.data);
  const { isGenderDisabled, isAgeDisabled, isCountryDisabled } = getEmptyCharts(
    insights?.data,
  );

  const isChartsDisabled = isEmpty;
  const isMetricsDisabled =
    isEstimatedPerformanceMetricsUnavailable ||
    benchmarks.status === 'error' ||
    !metricsToShow.length;

  const isAllAnalyticsDisabled = isChartsDisabled && isMetricsDisabled;
  // Insights and benchmarks load separately; acting before both settle would
  // select and save only the demographics, dropping the metrics.
  const isAnalyticsLoading = insights.isLoading || benchmarks.isInitialLoading;

  const creatorFollowerCount = useMemo(() => {
    if (!isCreator || !insights?.data) return undefined;

    const insightsData = insights.data as any;
    const creatorData = insightsData.creator;

    if (!creatorData) return undefined;

    if (platform === SOCIAL_PLATFORMS.instagram) {
      return creatorData.followersCount;
    }
    if (platform === SOCIAL_PLATFORMS.tiktok) {
      return creatorData.followersCount;
    }
    if (platform === SOCIAL_PLATFORMS.youtube) {
      return creatorData.subscriberCount;
    }

    return undefined;
  }, [isCreator, insights?.data, platform]);

  const isBelowMinimumFollowers =
    isCreator &&
    creatorFollowerCount !== undefined &&
    creatorFollowerCount < 100;

  const hasNoRecentContent = useMemo(() => {
    if (!isCreator || !benchmarks.isSuccess) return false;

    // Only check for YouTube for now
    if (platform !== SOCIAL_PLATFORMS.youtube) return false;

    const data = benchmarks.data;
    if (!data || !data.ytLastUpdatedAt) return false;

    const twentyEightDaysAgo = new Date();
    twentyEightDaysAgo.setDate(twentyEightDaysAgo.getDate() - 28);

    const lastUpdated = new Date(data.ytLastUpdatedAt);
    return lastUpdated < twentyEightDaysAgo;
  }, [isCreator, benchmarks.isSuccess, benchmarks.data, platform]);

  const isSelectAllDisabled = (() => {
    if (isMetricsDisabled) {
      // Only check demographics since metrics are disabled
      return selectedDemographics.length === demographics.length;
    }

    if (isChartsDisabled) {
      // Only check metrics since demographics are disabled
      return selectedMetrics.length === metricsToShow.length;
    }

    // Default: Both metrics and demographics are enabled
    return (
      selectedMetrics.length === metricsToShow.length &&
      selectedDemographics.length === demographics.length
    );
  })();

  const handleSelectAll = () => {
    const allDemographics = !isChartsDisabled ? demographics : [];
    const allMetrics = !isMetricsDisabled
      ? metricsToShow.map((m) => m.title)
      : [];

    setAllAnalytics(allDemographics, allMetrics);
  };

  return (
    <Modal.Instance
      open={isModalOpen}
      onOpenChange={(open) => {
        if (!open) {
          reset();
        }
      }}
    >
      <Modal.Root
        title={`Add ${
          SOCIAL_PLATFORMS_NAMES[platform as SOCIAL_PLATFORMS]
        } analytics`}
        size="lg"
        className="w-[1000px]"
        primaryAction={
          isConnected && !isAllAnalyticsDisabled
            ? {
                label: 'Save',
                onClick: handleSave,
                disabled: isAnalyticsLoading,
                buttonProps: {
                  'aria-label': 'Save',
                  'data-track-id': 'media-kit-platform-metrics-save-btn',
                },
              }
            : undefined
        }
        tertiaryAction={
          isConnected && !isAllAnalyticsDisabled
            ? {
                label: 'Select All',
                onClick: handleSelectAll,
                disabled: isAnalyticsLoading || isSelectAllDisabled,
                buttonProps: {
                  'aria-label': 'Select All',
                  'data-track-id': 'media-kit-platform-metrics-select-all-btn',
                },
              }
            : undefined
        }
      >
        {insights.isLoading || benchmarks.isLoading ? (
          <div className="flex flex-col gap-6">
            <PlatformDemographics isDemographicsDisabled={false} isLoading />
            <PlatformMetrics metricsToShow={[]} isLoading />
          </div>
        ) : isCreator ? (
          // For creators, check connection status, follower count, recent content, and analytics availability
          match({
            isConnected,
            isBelowMinimumFollowers,
            hasNoRecentContent,
            isAllAnalyticsDisabled,
          })
            // If Platform is not connected, render AnalyticsEmptyState with isConnected false
            .with({ isConnected: false }, () => (
              <AnalyticsEmptyState
                isDemographicsDisabled={true}
                platform={platform as SOCIAL_PLATFORMS}
                slug={creator.data?.slug}
                isConnected={false}
                isCreator={true}
              />
            ))
            // If below minimum followers, show DemographicsEmptyState with minimum follower message
            .with({ isBelowMinimumFollowers: true }, () => (
              <div className="flex flex-col gap-6">
                <DemographicsEmptyState
                  isChartsDisabled={true}
                  isBelowMinimumFollowers={true}
                />
                <MetricsEmptyState
                  platform={platform as SOCIAL_PLATFORMS}
                  isCreator={true}
                />
              </div>
            ))
            // If no recent content, show MetricsEmptyState with no recent content message
            .with({ hasNoRecentContent: true }, () => (
              <div className="flex flex-col gap-6">
                <PlatformDemographics
                  isDemographicsDisabled={isChartsDisabled}
                  isGenderDisabled={isGenderDisabled}
                  isAgeDisabled={isAgeDisabled}
                  isCountryDisabled={isCountryDisabled}
                  isLoading={insights.isLoading}
                  showEstimatedInfo={isEstimatedMetricsEnabled}
                />
                <MetricsEmptyState
                  platform={platform as SOCIAL_PLATFORMS}
                  isCreator={true}
                  hasNoRecentContent={true}
                />
              </div>
            ))
            // If all analytics are unavailable, render AnalyticsEmptyState
            .with({ isAllAnalyticsDisabled: true }, () => (
              <AnalyticsEmptyState
                isDemographicsDisabled={isChartsDisabled}
                platform={platform as SOCIAL_PLATFORMS}
                slug={creator.data?.slug}
                isConnected={true}
                isCreator={true}
              />
            ))
            // Otherwise, render the full analytics
            .otherwise(() => (
              <div className="flex flex-col gap-6">
                <PlatformDemographics
                  isDemographicsDisabled={isChartsDisabled}
                  isGenderDisabled={isGenderDisabled}
                  isAgeDisabled={isAgeDisabled}
                  isCountryDisabled={isCountryDisabled}
                  isLoading={insights.isLoading}
                  showEstimatedInfo={isEstimatedMetricsEnabled}
                />
                <PlatformMetrics
                  metricsToShow={metricsToShow}
                  isLoading={benchmarks.isLoading}
                />
                {isDisconnected ? (
                  <DisconnectedCard
                    lastUpdatedDate={formatDate(getLastUpdatedDate ?? '')}
                    platformFullAccess={creator.data?.platformFullAccess}
                  />
                ) : null}
              </div>
            ))
        ) : (
          // For managers, check multiple conditions
          match({
            isConnected,
            isAllAnalyticsDisabled,
            isChartsDisabled,
            isMetricsDisabled,
          })
            // If Platform is not connected, render AnalyticsEmptyState with isConnected false
            .with({ isConnected: false }, () => {
              return (
                <AnalyticsEmptyState
                  isDemographicsDisabled={true}
                  platform={platform as SOCIAL_PLATFORMS}
                  slug={creator.data?.slug}
                  isConnected={false}
                />
              );
            })
            // If Audience Demographics and Metrics are unavailable, render AnalyticsEmptyState
            .with({ isAllAnalyticsDisabled: true }, () => {
              return (
                <AnalyticsEmptyState
                  isDemographicsDisabled={isChartsDisabled}
                  platform={platform as SOCIAL_PLATFORMS}
                  slug={creator.data?.slug}
                  isConnected={true}
                />
              );
            })
            // If Audience Demographics is unavailable, show DemographicsEmptyState
            .with({ isChartsDisabled: true }, () => (
              <div className="flex flex-col gap-6">
                <DemographicsEmptyState isChartsDisabled={isChartsDisabled} />
                <PlatformMetrics
                  metricsToShow={metricsToShow}
                  isLoading={benchmarks.isLoading}
                />
                {isDisconnected ? (
                  <DisconnectedCard
                    lastUpdatedDate={formatDate(getLastUpdatedDate ?? '')}
                    platformFullAccess={creator.data?.platformFullAccess}
                  />
                ) : null}
              </div>
            ))
            // If Audience Metrics is unavailable, show MetricsEmptyState
            .with({ isMetricsDisabled: true }, () => (
              <div className="flex flex-col gap-6">
                <PlatformDemographics
                  isDemographicsDisabled={isChartsDisabled}
                  isGenderDisabled={isGenderDisabled}
                  isAgeDisabled={isAgeDisabled}
                  isCountryDisabled={isCountryDisabled}
                  isLoading={insights.isLoading}
                  showEstimatedInfo={isEstimatedMetricsEnabled}
                />
                <MetricsEmptyState
                  platform={platform as SOCIAL_PLATFORMS}
                  showConnectMessage={isEstimatedPerformanceMetricsUnavailable}
                  slug={creator.data?.slug}
                />
                {isDisconnected ? (
                  <DisconnectedCard
                    lastUpdatedDate={formatDate(getLastUpdatedDate ?? '')}
                    platformFullAccess={creator.data?.platformFullAccess}
                  />
                ) : null}
              </div>
            ))
            // If both Audience Demographics and Metrics available, render the components
            .otherwise(() => (
              <div className="flex flex-col gap-6">
                <PlatformDemographics
                  isDemographicsDisabled={isChartsDisabled}
                  isGenderDisabled={isGenderDisabled}
                  isAgeDisabled={isAgeDisabled}
                  isCountryDisabled={isCountryDisabled}
                  isLoading={insights.isLoading}
                  showEstimatedInfo={isEstimatedMetricsEnabled}
                />
                <PlatformMetrics
                  metricsToShow={metricsToShow}
                  isLoading={benchmarks.isLoading}
                />
                {isDisconnected ? (
                  <DisconnectedCard
                    lastUpdatedDate={formatDate(getLastUpdatedDate ?? '')}
                    platformFullAccess={creator.data?.platformFullAccess}
                  />
                ) : null}
              </div>
            ))
        )}
      </Modal.Root>
    </Modal.Instance>
  );
};
